import { useEffect, useRef, useCallback, useState } from 'react';

interface SwipeNavigationOptions {
  /**
   * Minimum horizontal distance (in pixels) required for a swipe to be registered
   * Default: 50
   */
  threshold?: number;
  
  /**
   * Maximum vertical distance allowed during a horizontal swipe
   * If exceeded, the swipe is ignored (user might be scrolling vertically)
   * Default: 100
   */
  maxVerticalDistance?: number;
  
  /**
   * Maximum time (in ms) allowed for a quick swipe gesture.
   * Slower gestures can still succeed if they exceed longSwipeDistance.
   * Default: 300
   */
  maxTime?: number;

  /**
   * Distance (in pixels) at which a slow drag always counts as a swipe,
   * regardless of how long it took. Prevents "dragged all the way but nothing happened".
   * Default: 120
   */
  longSwipeDistance?: number;
  
  /**
   * Whether swipe navigation is enabled
   * Default: true
   */
  enabled?: boolean;
  
  /**
   * Callback when user swipes left (next)
   */
  onSwipeLeft?: () => void;
  
  /**
   * Callback when user swipes right (previous)
   */
  onSwipeRight?: () => void;
  
  /**
   * Callback during drag with current offset
   * Use this to apply visual feedback (e.g., translateX transform)
   */
  onDrag?: (offset: number) => void;
  
  /**
   * Callback when drag ends (before navigation)
   * Use this to reset transforms or apply snap animation
   */
  onDragEnd?: () => void;
}

interface TouchData {
  startX: number;
  startY: number;
  startTime: number;
  isDragging: boolean;
}

/**
 * Custom hook for detecting swipe gestures on touch devices
 * Used for calendar navigation (swipe left = next, swipe right = previous)
 * 
 * @param ref - React ref to the element to attach touch listeners to
 * @param options - Configuration options for swipe detection
 */
export function useSwipeNavigation(
  ref: React.RefObject<HTMLElement>,
  options: SwipeNavigationOptions = {}
) {
  const {
    threshold = 50,
    maxVerticalDistance = 100,
    maxTime = 300,
    longSwipeDistance = 120,
    enabled = true,
    onSwipeLeft,
    onSwipeRight,
    onDrag,
    onDragEnd
  } = options;

  const touchDataRef = useRef<TouchData | null>(null);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled || e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    touchDataRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      isDragging: false
    };
  }, [enabled]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || !touchDataRef.current || e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    const { startX, startY } = touchDataRef.current;
    
    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    
    // Only start dragging if horizontal movement is significant and greater than vertical
    if (absX > 50 && absX > absY) {
      if (!touchDataRef.current.isDragging) {
        touchDataRef.current.isDragging = true;
      }
      
      // Call onDrag with current offset
      onDrag?.(deltaX);
      
      // Prevent default scrolling if we're in a horizontal drag
      if (absY < maxVerticalDistance) {
        e.preventDefault();
      }
    }
  }, [enabled, maxVerticalDistance, onDrag]);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!enabled || !touchDataRef.current || e.changedTouches.length !== 1) {
      touchDataRef.current = null;
      onDragEnd?.();
      return;
    }

    const touch = e.changedTouches[0];
    const { startX, startY, startTime, isDragging } = touchDataRef.current;
    
    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;
    const deltaTime = Date.now() - startTime;
    
    // Reset touch data
    touchDataRef.current = null;
    
    // Notify drag end
    onDragEnd?.();

    // Check if swipe is valid
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    // A swipe is valid if:
    // 1. User was dragging (not a tap)
    // 2. Vertical distance is within tolerance (not a scroll)
    // 3. Either: quick flick (within maxTime and past threshold)
    //    OR: long drag past longSwipeDistance (always counts regardless of time)
    if (!isDragging || absY > maxVerticalDistance) {
      return;
    }

    const isQuickSwipe = absX >= threshold && deltaTime <= maxTime;
    const isLongDrag = absX >= longSwipeDistance;

    if (!isQuickSwipe && !isLongDrag) {
      return;
    }

    // Determine swipe direction
    if (deltaX < 0) {
      // Swipe left = go to next
      onSwipeLeft?.();
    } else {
      // Swipe right = go to previous
      onSwipeRight?.();
    }
  }, [enabled, threshold, maxVerticalDistance, maxTime, longSwipeDistance, onSwipeLeft, onSwipeRight, onDragEnd]);

  const handleTouchCancel = useCallback(() => {
    touchDataRef.current = null;
    onDragEnd?.();
  }, [onDragEnd]);

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;

    // Use passive: false for touchmove to allow preventDefault when horizontal dragging
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    element.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [ref, enabled, handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel]);
}

export default useSwipeNavigation;
